import type { OnboardingRequirement, RequirementConfirmations } from '@onefootprint/request-types';
import type { AnyRouter } from '@tanstack/react-router';
import { getLogger, trackAction } from '@/idv2/utils/logger';

const { logError } = getLogger({ name: 'idv2', location: 'route-tracking' });

// Module-level state for tracking
let prevActionName: string | null = null;
let prevActionStartTime: number | undefined;
let prevRequirement: OnboardingRequirement | undefined;
let prevRequirementStartTime: number | undefined;
let prevPathname: string | undefined;
// Set by trackPreviousRouteOutcome/trackCurrentRouteCompleted on real user navigation,
// consumed by trackRequirementTransition to distinguish genuine navigations from
// TanStack Router re-evaluating the same child route's beforeLoad.
let navigationOccurred = false;
// When true, the next trackRequirementTransition call skips stuck detection.
// Used when a forward navigation is expected to land on the same requirement
// (e.g. verified-prefill-overlay vaults dob field then finds more data needed so we get back collect-data again).
let expectSameRequirement = false;

/**
 * Generate a unique key for a requirement based on its type and relevant properties.
 * Based on the proven requirementKey pattern from IDV.
 */
const getRequirementKey = (requirement: OnboardingRequirement): string => {
  if (requirement.kind === 'collect_document') {
    if (requirement.config.kind === 'custom') {
      return `${requirement.kind}-${requirement.config.name}`;
    }
    return `${requirement.kind}-${requirement.config.kind}-${requirement.documentRequestId}`;
  }
  if (requirement.kind === 'collect_custom_data') {
    const dis = requirement.page.sections
      .filter(s => s.kind === 'prompt')
      .map(s => s.di)
      .join(',');
    return `${requirement.kind}-${dis}`;
  }
  if (requirement.kind === 'sleep') {
    // Sleep requirements should never be considered repeats
    return `${requirement.kind}-${Date.now()}`;
  }
  if (requirement.kind === 'select_conditional_branch') {
    return `${requirement.kind}-${requirement.nodeName ?? ''}`;
  }
  return requirement.kind;
};

const getActionName = (pathname: string): string | null => {
  // Skip intermediate/redirect routes
  if (pathname.includes('/check-requirements/')) return null;
  if (pathname === '/' || pathname === '') return null;

  // Special cases
  if (pathname === '/complete') return 'onboarding';
  if (pathname.startsWith('/identify')) return 'identify';

  // Extract the last meaningful segment from the path
  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] || null;

  // Skip if the last segment is a token (starts with common token prefixes)
  // These are dynamic route params, not meaningful action names
  if (lastSegment && /^(utok_|obtok_|tok_|[a-f0-9-]{36})/i.test(lastSegment)) {
    return null;
  }

  return lastSegment;
};

const isSameRequirement = (
  prev: OnboardingRequirement | undefined,
  current: OnboardingRequirement | undefined,
): boolean => {
  if (!prev || !current) return false;
  if (prev.kind === 'sleep' && current.kind === 'sleep') return false;
  return getRequirementKey(prev) === getRequirementKey(current);
};

/** Text-only collect_custom_data (no prompt sections) used as async wait gates; exclude from stuck detection. */
const isTextOnlyCollectCustomData = (req: OnboardingRequirement | undefined): boolean =>
  req?.kind === 'collect_custom_data' && req.page.sections.every(s => s.kind !== 'prompt');

/**
 * Manually track the completed event for the current route.
 * Call this before relaying to legacy idv, since the router will be abandoned.
 */
export const trackCurrentRouteCompleted = () => {
  const now = Date.now();

  if (prevActionName && prevActionName !== 'onboarding') {
    const durationMs = prevActionStartTime ? now - prevActionStartTime : undefined;
    trackAction(`${prevActionName}:completed`, { durationMs });
  }

  // The user may come back from legacy IDV, and we need to preserve
  // prevRequirement and prevRequirementStartTime to detect stuck patterns.

  // Only reset action-related state (route tracking will be handled by legacy IDV)
  prevActionName = null;
  prevActionStartTime = undefined;
  prevPathname = undefined;
  navigationOccurred = true;
};

/**
 * Track the outcome of the previous route based on navigation direction.
 * Called from check-requirements with the actual navigation value from context.
 */
export const trackPreviousRouteOutcome = (navigation: 'forward' | 'back' | undefined) => {
  const now = Date.now();

  if (prevActionName && prevActionName !== 'onboarding') {
    const durationMs = prevActionStartTime ? now - prevActionStartTime : undefined;
    if (navigation === 'back') {
      trackAction(`${prevActionName}:back`, { durationMs });
    } else {
      trackAction(`${prevActionName}:completed`, { durationMs });
    }
  }

  // Reset action tracking state to avoid double-tracking in trackCurrentRouteCompleted
  prevActionName = null;
  prevActionStartTime = undefined;
  navigationOccurred = true;
};

/**
 * Track requirement transition - detects stuck users and tracks time spent.
 * Called from check-requirements with the actual values from context.
 */
export const trackRequirementTransition = (
  currentRequirement: OnboardingRequirement | undefined,
  confirmations: RequirementConfirmations | undefined,
  pathname: string,
) => {
  // TanStack Router can re-run a child route's beforeLoad without a real navigation event
  // (e.g. during redirect resolution). When no navigation occurred and we see the same
  // requirement on the same path, skip entirely to avoid false stuck errors and bogus
  // time_spent entries.
  if (!navigationOccurred && isSameRequirement(prevRequirement, currentRequirement) && prevPathname === pathname) {
    return;
  }
  navigationOccurred = false;

  const now = Date.now();
  const durationMs = prevRequirementStartTime ? now - prevRequirementStartTime : undefined;

  // Detect if user navigated but ended up at the same requirement AND same route
  // (unless confirmations is 'wait', which is intentional).
  // Same requirement with different pathname is legitimate (e.g. collect-business-data sub-routes).
  // Skip stuck detection for text-only collect_custom_data (async wait gates) to avoid false positives.
  if (expectSameRequirement) {
    expectSameRequirement = false;
  } else if (
    confirmations !== 'wait' &&
    !isTextOnlyCollectCustomData(currentRequirement) &&
    isSameRequirement(prevRequirement, currentRequirement) &&
    prevPathname === pathname
  ) {
    // The key, not the requirement object: that carries the full page/section config.
    const requirementKey = currentRequirement ? getRequirementKey(currentRequirement) : 'unknown';
    logError(
      `User is stuck on a requirement. Detected same requirement after navigation: ${requirementKey}`,
      undefined,
      {
        requirementKind: currentRequirement?.kind,
        pathname,
        timeSpentMs: durationMs,
      },
    );
  }

  // Track time spent on previous requirement
  if (prevRequirement && durationMs !== undefined) {
    trackAction('requirement:time_spent', {
      requirementKind: prevRequirement.kind,
      durationMs,
    });
  }

  // Update requirement tracking state
  prevRequirement = currentRequirement;
  prevRequirementStartTime = currentRequirement ? now : undefined;
  prevPathname = pathname;
};

/**
 * Call before a forward navigation that is expected to land on the same requirement.
 * Suppresses the stuck-user error for exactly one transition cycle.
 */
export const suppressNextStuckDetection = () => {
  expectSameRequirement = true;
};

export const setupRouteTracking = (router: AnyRouter) => {
  return router.subscribe('onResolved', ({ toLocation }) => {
    const currentPath = toLocation.pathname;
    const currentAction = getActionName(currentPath);
    const now = Date.now();

    // Track started for current route only if the action has changed
    // This prevents duplicate :started events when the router resolves multiple times
    if (currentAction && currentAction !== prevActionName) {
      trackAction(currentPath === '/complete' ? 'onboarding:completed' : `${currentAction}:started`);
    }

    prevActionName = currentAction;
    prevActionStartTime = currentAction ? now : undefined;
  });
};
